class DropdownMenu extends HTMLElement {
  constructor() {
    super();
    this.closeTimeout = null;

    this.toggleBtn = null;
    this.list = null;
    this.portalList = null;
    this.isClickMode = false;

    this.openMenu = this.openMenu.bind(this);
    this.scheduleClose = this.scheduleClose.bind(this);
    this.cancelClose = this.cancelClose.bind(this);
    this.handleClickToggle = this.handleClickToggle.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.toggleBtn = this.querySelector(".dropdown-toggle");
    this.list = this.querySelector(".dropdown-list");
    if (!this.list || !this.toggleBtn) return;

    // Ensure global container exists
    this.globalContainer = document.querySelector(".global-dropdown-container");
    if (!this.globalContainer) {
      this.globalContainer = document.createElement("div");
      this.globalContainer.classList.add("global-dropdown-container");
      document.body.appendChild(this.globalContainer);
    }

    // Give each dropdown-menu a unique id (needed for portal reference)
    if (!this.id)
      this.id = `dropdown-${Math.random().toString(36).substr(2, 9)}`;

    this.updateMode();
    window.addEventListener("resize", this.handleResize);

    this.initNestedDropdowns();
  }

  disconnectedCallback() {
    this.portalList?.remove();
    window.removeEventListener("resize", this.handleResize);
  }

  /** Initialize nested dropdowns for desktop hover */
  initNestedDropdowns() {
    this.querySelectorAll(".nested-dropdown").forEach((nested) => {
      const btn = nested.querySelector(".dropdown-toggle");
      const nestedList = nested.querySelector(".dropdown-list");
      if (!btn || !nestedList) return;

      nestedList.style.display = "none";

      // Desktop hover
      nested.addEventListener("mouseenter", () => {
        if (!this.isClickMode) nestedList.style.display = "block";
      });
      nested.addEventListener("mouseleave", () => {
        if (!this.isClickMode) nestedList.style.display = "none";
      });
    });
  }

  /** Update click/hover mode & portal */
  updateMode() {
    this.isClickMode = window.innerWidth <= 500;

    // Remove previous events
    this.toggleBtn.removeEventListener("click", this.handleClickToggle);
    this.removeEventListener("mouseenter", this.openMenu);
    this.removeEventListener("mouseleave", this.scheduleClose);

    if (this.isClickMode) {
      // Move dropdown list to portal
      if (!this.portalList) {
        this.portalList = this.list.cloneNode(true);
        this.portalList.classList.add("portal-dropdown");
        this.portalList.style.position = "absolute";
        this.portalList.style.display = "none";

        // Reference to parent dropdown-menu
        this.portalList.dataset.parentDropdownId = this.id;

        // Hide all nested dropdowns initially
        this.portalList
          .querySelectorAll(".nested-dropdown > .dropdown-list")
          .forEach((list) => {
            list.style.display = "none";
          });

        // Add click toggle for nested dropdowns in portal (accordion behavior)
        this.portalList
          .querySelectorAll(".nested-dropdown > .dropdown-toggle")
          .forEach((btn) => {
            btn.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              const nestedList = btn.nextElementSibling;
              if (!nestedList) return;

              // Close all other nested dropdowns in this portal
              this.portalList
                .querySelectorAll(".nested-dropdown > .dropdown-list")
                .forEach((list) => {
                  if (list !== nestedList) list.style.display = "none";
                });

              // Toggle this nested dropdown
              nestedList.style.display =
                nestedList.style.display === "block" ? "none" : "block";
            });
          });

        this.globalContainer.appendChild(this.portalList);
      }
      this.toggleBtn.addEventListener("click", this.handleClickToggle);
    } else {
      // Remove portal and show original inline list
      if (this.portalList) this.portalList.style.display = "none";
      this.list.style.display = "none";
      this.addEventListener("mouseenter", this.openMenu);
      this.addEventListener("mouseleave", this.scheduleClose);
    }
  }

  handleResize() {
    this.updateMode();
  }

  openMenu() {
    this.cancelClose();
    this.list.style.display = "block";
    this.classList.add("open");
  }

  scheduleClose() {
    this.closeTimeout = setTimeout(() => {
      this.list.style.display = "none";
      this.classList.remove("open");
      this.closeNestedDropdowns();
    }, 150);
  }

  cancelClose() {
    clearTimeout(this.closeTimeout);
  }

  handleClickToggle(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!this.portalList) return;

    const isOpen = this.portalList.style.display === "block";

    // Close all other portal dropdowns
    document.querySelectorAll(".portal-dropdown").forEach((p) => {
      p.style.display = "none";
      // Remove open class from their parent dropdown-menu
      if (p.dataset.parentDropdownId) {
        document
          .getElementById(p.dataset.parentDropdownId)
          ?.classList.remove("open");
      }
      // Close nested dropdowns
      p.querySelectorAll(".nested-dropdown > .dropdown-list").forEach(
        (list) => {
          list.style.display = "none";
        },
      );
    });

    if (!isOpen) {
      // Position portal dropdown below button
      const rect = this.toggleBtn.getBoundingClientRect();
      this.portalList.style.top = rect.bottom + window.scrollY + "px";
      this.portalList.style.left = rect.left + window.scrollX + "px";
      this.portalList.style.minWidth = rect.width + "px";
      this.portalList.style.display = "block";

      // Add open class to this dropdown-menu
      this.classList.add("open");
    } else {
      this.portalList.style.display = "none";
      this.classList.remove("open");
    }
  }

  closeNestedDropdowns() {
    this.querySelectorAll(".nested-dropdown > .dropdown-list").forEach(
      (list) => {
        list.style.display = "none";
      },
    );
  }
}

customElements.define("dropdown-menu", DropdownMenu);
